"use client";

import { useState } from "react";
import { useTRPC } from "@/trpc/react";
import { useQuery } from "@tanstack/react-query";

const ITEM_TYPES = ["All", "Weapon", "Armor", "Helmet", "Shield", "Amulet", "Ring", "Cosmetic"];
const SORT_OPTIONS = [
  { value: "newest" as const, label: "Newest" },
  { value: "price_asc" as const, label: "Price: Low → High" },
  { value: "price_desc" as const, label: "Price: High → Low" },
];

const RARITY_COLORS: Record<string, string> = {
  Common: "#6a7a5a", Uncommon: "#4a9e3f", Rare: "#5b9ec4",
  Epic: "#9b59b6", Legendary: "#d4a853", Mythic: "#c0392b",
};

const ITEM_ICONS: Record<string, string> = {
  Weapon: "⚔️", Armor: "🛡️", Helmet: "⛑️", Shield: "🔰",
  Amulet: "📿", Ring: "💍", Cosmetic: "✨", Pet: "🐉",
};

export default function MarketplacePage() {
  const trpc = useTRPC();
  const [itemType, setItemType] = useState("All");
  const [sortBy, setSortBy] = useState<"newest" | "price_asc" | "price_desc">("newest");

  const { data, isLoading } = useQuery(
    trpc.gameMarketplace.getListings.queryOptions({
      sortBy,
      limit: 20,
    })
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <h1 className="text-3xl font-black text-amber-400 uppercase tracking-wider mb-2">Marketplace</h1>
      <p className="text-sm text-white/25 mb-8">Buy and sell NFT gear. 4% fee on every sale goes to the Treasury.</p>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-8">
        <div className="flex flex-wrap gap-2">
          {ITEM_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setItemType(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                itemType === t
                  ? "bg-green-500 text-black"
                  : "bg-white/5 border border-white/10 text-white/40 hover:border-green-500/30"
              }`}
            >
              {t === "All" ? t : `${ITEM_ICONS[t] ?? ""} ${t}`}
            </button>
          ))}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          className="px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-white/50 font-bold"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Listings */}
      {isLoading ? (
        <div className="text-center py-20 text-white/25">Loading marketplace...</div>
      ) : !data?.listings || data.listings.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-white/40 text-lg font-bold">No items listed yet.</p>
          <p className="text-white/25 text-sm mt-2">Be the first to list your loot!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.listings.map((listing) => (
            <div key={listing.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 hover:border-green-500/30 transition-colors">
              <div className="text-center text-3xl mb-3">
                {ITEM_ICONS["Weapon"] ?? "📦"}
              </div>
              <h3 className="text-sm font-black text-amber-400 uppercase text-center mb-2">
                Listed Item
              </h3>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                <span className="text-lg font-black text-amber-400">{listing.price} AVAX</span>
                <button className="px-4 py-2 rounded-lg bg-green-500 text-black text-sm font-black uppercase hover:bg-green-600 transition-colors">
                  Buy
                </button>
              </div>
              <p className="text-xs text-white/20 mt-2 font-mono truncate">
                Seller: {listing.seller.displayName ?? listing.seller.walletAddress}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
